import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { claudeChatAPI, claudeChatEventsUrl } from '../../services/api';
import { createSseParser } from './sse';
import { reduceEvents } from './reduceEvents';
import { isDisabledError, shouldReconnect, streamOutcome } from './shouldReconnect';

const RECONNECT_MS = 2000;
const reducer = (items, action) => (action.reset ? [] : reduceEvents(items, action.frame));

// onDisabled() fires when the feature has been switched off and the stream must not be reopened.
export default function useChatStream(sessionId, onDisabled) {
  const [items, dispatch] = useReducer(reducer, []);
  const [running, setRunning] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const lastId = useRef(0);
  const onDisabledRef = useRef(onDisabled);
  onDisabledRef.current = onDisabled;

  useEffect(() => {
    if (!sessionId) return undefined;
    dispatch({ reset: true });
    lastId.current = 0;
    setError('');
    let cancelled = false;
    let timer;
    let controller;

    const switchedOff = () => {
      setConnected(false);
      setError('Claude Code chat is switched off.');
      if (onDisabledRef.current) onDisabledRef.current();
    };

    const connect = async () => {
      controller = new AbortController();
      try {
        const res = await fetch(claudeChatEventsUrl(sessionId), {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            ...(lastId.current ? { 'Last-Event-ID': String(lastId.current) } : {}),
          },
        });
        let code;
        if (res.status === 403) code = (await res.json().catch(() => null))?.code;
        if (cancelled) return;
        const outcome = streamOutcome(res.status, code);
        if (outcome === 'disabled') { switchedOff(); return; }
        if (outcome === 'expired') { setConnected(false); setError('Session expired. Sign in again.'); return; }
        // Retrying cannot fix these, so stop instead of polling every couple of seconds.
        if (outcome === 'notfound') { setConnected(false); setError('Chat not found.'); return; }
        if (outcome === 'fatal') { setConnected(false); setError(`Could not open the chat (error ${res.status}).`); return; }
        if (outcome !== 'ok') throw new Error(`stream ${res.status}`);
        setConnected(true);
        setError('');
        const parser = createSseParser((frame) => {
          if (Number.isFinite(frame.id)) lastId.current = Math.max(lastId.current, frame.id);
          dispatch({ frame });
          if (frame.type === 'user_message') setRunning(true);
          if (frame.type === 'done' || frame.type === 'error') setRunning(false);
        });
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          parser.push(dec.decode(value, { stream: true }));
        }
      } catch (e) {
        if (cancelled || e.name === 'AbortError') return;
      }
      if (cancelled) return;
      setConnected(false);

      // The stream ended or failed. Ask the server whether we may still be connected before retrying.
      try {
        const status = (await claudeChatAPI.status()).data.data;
        if (cancelled) return;
        if (!shouldReconnect(status)) { switchedOff(); return; }
      } catch (e) {
        if (cancelled) return;
        // Status unreachable (network blip): fall through and retry like any dropped connection.
      }
      timer = setTimeout(connect, RECONNECT_MS); // dropped: replay from Last-Event-ID
    };

    // Whether a run is already in flight when we (re)open the page.
    claudeChatAPI.session(sessionId).then((r) => setRunning(!!r.data.data.running)).catch((e) => {
      if (!cancelled && isDisabledError(e)) switchedOff();
    });
    connect();
    return () => { cancelled = true; clearTimeout(timer); controller?.abort(); };
  }, [sessionId]);

  // send resolves true on success so the caller can keep the draft when it fails.
  const send = useCallback(async (text) => {
    try { await claudeChatAPI.send(sessionId, text); setError(''); return true; } catch (e) {
      if (isDisabledError(e) && onDisabledRef.current) { onDisabledRef.current(); return false; }
      setError(e.response?.data?.message || 'Could not send');
      return false;
    }
  }, [sessionId]);
  const stop = useCallback(async () => {
    try { await claudeChatAPI.stop(sessionId); setRunning(false); } catch (e) {
      if (isDisabledError(e) && onDisabledRef.current) onDisabledRef.current();
    }
  }, [sessionId]);
  // decide resolves { ok, message }; the card shows the message, so the page-level error stays clean.
  const decide = useCallback(async (approvalId, decision) => {
    try { await claudeChatAPI.decide(approvalId, decision); setError(''); return { ok: true }; } catch (e) {
      if (isDisabledError(e) && onDisabledRef.current) { onDisabledRef.current(); return { ok: false, message: 'Switched off' }; }
      return { ok: false, message: e.response?.data?.message || 'Could not record the decision' };
    }
  }, []);

  return { items, running, connected, error, send, stop, decide };
}
